import { ElementApi } from 'platejs'
import type { TNode } from 'platejs'

import type { SerializedNode } from '~publish/legacy/editor/BufferEditor/types'
import type { PlainText } from '~publish/legacy/editor/BufferEditor/types.plate'
import type { WithPosition } from '~publish/legacy/editor/helpers/WithPosition'

import type { AutocompleteData } from '../../autocomplete'
import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from '../../autocomplete/nodes'

export const LINKEDIN_MENTION_INPUT_ELEMENT_TYPE = 'linkedin-mention-input'
export const LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER = '@'
export const LINKEDIN_MENTION_INPUT_VALID_TEXT_REGEX = /([a-zA-Z0-9 ._-]+)/

export enum SupportedEntity {
  Company = 'company',
  School = 'school',
  Showcase = 'showcase',
}

/**
 * The detail properties of a linkedin annotation entity
 * after it's been parsed when loading the composer.
 * It is extended below to include the indices of the entity.
 */
export interface LinkedInAnnotationEntityDetails extends AutocompleteData {
  // for personal profiles it's a string, for organizations it's a number
  id: string | number
  entity: string
  vanityName: string
  localizedName: string
  link: string
}

export type LinkedInAnnotationEntity =
  WithPosition<LinkedInAnnotationEntityDetails>

export interface LinkedInAnnotationInputElement extends AutocompleteInputElement {
  type: typeof LINKEDIN_MENTION_INPUT_ELEMENT_TYPE
  trigger: typeof LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER
  children: PlainText[]
  valid: boolean
  autocompleted: boolean
  channelId?: string
  isPermissionError?: boolean
  isLoading?: boolean
  notFound?: boolean
  data?: LinkedInAnnotationEntityDetails
}

export type LinkedInAnnotationInputElementInterface = Required<
  AutocompleteInputElementInterface<LinkedInAnnotationInputElement>
>

export const LinkedInAnnotationInputElement: LinkedInAnnotationInputElementInterface =
  {
    type: LINKEDIN_MENTION_INPUT_ELEMENT_TYPE,
    trigger: LINKEDIN_MENTION_INPUT_ELEMENT_TRIGGER,
    validTextRegex: LINKEDIN_MENTION_INPUT_VALID_TEXT_REGEX,
    is(node: TNode): node is LinkedInAnnotationInputElement {
      return (
        ElementApi.isElement(node) &&
        node.type === LinkedInAnnotationInputElement.type
      )
    },

    new: (data?: LinkedInAnnotationEntityDetails, initialQuery?: string) => {
      return {
        type: LinkedInAnnotationInputElement.type,
        children: [{ text: '' }],
        // If data is provided, the mention is autocompleted and valid
        // This is because all LinkedIn mentions are invalid until autocompleted
        valid: !!data,
        autocompleted: !!data,
        trigger: LinkedInAnnotationInputElement.trigger,
        data,
        initialQuery,
      }
    },

    toEntity(
      node: LinkedInAnnotationInputElement,
    ): LinkedInAnnotationEntityDetails | undefined {
      return node.data
    },

    serialize(
      node: LinkedInAnnotationInputElement,
    ): SerializedNode<LinkedInAnnotationEntityDetails | undefined> {
      // The node is void — children never carry its display text, so
      // serialize() (used for the whole document's plain-text output, e.g.
      // character counting and other mentions' position indices) has to
      // supply it here instead of via a child text node.
      return {
        text: node.data?.localizedName ?? '',
        data: LinkedInAnnotationInputElement.toEntity(node),
      }
    },
  }
